<script setup lang="ts">
import { ApiHomepage } from '~~/server/api/homepage'

const { data: page } = await useLazyFetch<ApiHomepage>('/api/homepage')
const config = useRuntimeConfig()

// Schema.org
useSchemaOrg([
    defineOrganization({
        name: config.public.schema.organization.name,
        logo: `${config.app.cdnURL}/${config.public.PUBLIC_IMAGES_URL}/logo.svg`,
        description: page.value?.data.meta.meta_description || '',
        keywords: page.value?.data.meta.meta_keywords || '',
        postalAddress: {
            addressLocality: config.public.schema.organization.addressLocality,
            addressRegion: config.public.schema.organization.addressRegion,
            postalCode: config.public.schema.organization.postalCode,
            streetAddress: config.public.schema.organization.streetAddress,
        },
        contactPoint: [
            {
                telephone: config.public.schema.organization.phone,
                contactType: config.public.schema.organization.contactType,
                areaServed: config.public.schema.organization.areaServed,
                availableLanguage:
                    config.public.schema.organization.availableLanguage,
            },
        ],
    }),
])

// Page meta
usePageMeta({
    meta: page.value?.data.meta,
    title: page.value?.data.title,
})
</script>
<template>
  <article v-if="page">
    <WidgetReels
      v-if="page.data.video_headline"
      :data="page.data.video_headline"
      :title="page.data.title"
      :subtitle="page.data.subtitle"
      :flash-links="page.data.flash_links"
    />
    <div class="content-visibility-auto">
      <ContainerFluid>
        <Banner
          v-if="page.data.main_banner"
          heading-level="h2"
          :data="page.data.main_banner"
          class="mb-12 mb-md-20"
        >
          <template
            v-if="page?.data?.main_banner?.tag?.background && page?.data?.main_banner?.tag?.name"
            #tag
          >
            <Label
              :text-color="page?.data?.main_banner?.tag?.color"
              :label="page?.data?.main_banner?.tag?.name"
              :background-color="page?.data?.main_banner?.tag?.background"
            />
          </template>
        </Banner>
        <LazyWidgetBestsellers
          class="mb-12 mb-md-20"
          heading-level="h2"
          :data="page.data.bestsellers"
        />
        <WidgetFlashNews
          class="mb-12 mb-md-20"
          heading-level="h2"
          :data="page.data.flash_news.data"
          :title="page.data.flash_news.title"
          :url="page.data.flash_news.url"
        />
        <LazyGallery
          v-if="page.data.gallery?.data[0]?.file?.path"
          class="mb-12 mb-md-20"
          :data="page.data.gallery"
          :thumbnail="page.data.gallery.data[0].file.path"
          :extra="page.data.gallery?.data[0]?.title"
          heading-level="h2"
        />
        <LazyVVideo
          v-if="page.data.video"
          heading-level="h2"
          class="pb-12 pb-md-14"
          :data="page.data.video"
        />
        <LazyWidgetProductsNews
          class="mb-12 mb-md-20"
          heading-level="h2"
          :data="page.data.news"
        />
        <LazyBox
          classes="mb-12 mb-md-20 rounded"
          padding="p-2 p-md-6"
          decor-position="top-right"
          color="gray"
        >
          <div
            class="row"
            data-cy="row_benefits_careers"
          >
            <div
              v-if="page.data.benefits"
              class="col-12 col-lg-6 mb-2 mb-md-6 mb-lg-0"
            >
              <WidgetBenefits
                class="h-100"
                :data="page.data.benefits"
              />
            </div>
            <div
              v-if="page.data.careers"
              class="col-12 col-lg-6"
            >
              <WidgetCareer
                class="h-100"
                :data="page.data.careers"
                :heading-level="'h2'"
              />
            </div>
          </div>
        </LazyBox>
        <LazyBanner
          v-if="page.data.foundation_banner"
          heading-level="h2"
          :data="page.data.foundation_banner"
          class="mb-20"
        >
          <template #tag>
            <img
              width="137"
              height="56"
              src="~/assets/images/kooperativa-nadation.png"
              loading="lazy"
              decoding="sync"
              alt="Kooperatíva nadácia"
            >
          </template>
        </LazyBanner>
        <LazyWidgetArticles
          class="mb-10 mb-md-20"
          heading-level="h2"
          :data="page.data.magazine"
        />
        <LazyWidgetSocials
          class="mb-12 mb-md-20"
          :data="page.data.social"
        />
        <div class="pt-12 pt-md-20 border-top">
          <LazyWidgetHelpdeskExt />
        </div>
      </ContainerFluid>
    </div>
  </article>
</template>
